import BootstrapTable from "react-bootstrap-table-next";
import overlayFactory from "react-bootstrap-table2-overlay";
import Spinner from "react-bootstrap/Spinner";
import { Badge, Button, UncontrolledTooltip } from "reactstrap";
import { EMPTY_LIST } from "src/common/constants";
import UsageDecision from "./UsageDecision";
import {
    formatControlChip,
    getMeasureTypeLabel,
    getVisibleControls,
} from "../helpers/measureList";

const PositiveListTable = ({
    items,
    loading,
    isFetched,
    filtersAreActive,
    pendingMeasureId,
    deletingMeasureId,
    onUsageChange,
    onViewMeasure,
    onDeleteMeasure,
    t,
}) => {
    const columns = [
        {
            dataField: "title",
            text: t("Measure"),
            sort: false,
            headerStyle: { width: "26%", minWidth: "220px" },
            style: { width: "26%", minWidth: "220px", whiteSpace: "normal" },
            formatter: (cellContent, row) => (
                <button
                    type="button"
                    className="btn btn-link positive-list__measure-name"
                    onClick={() => onViewMeasure(row)}
                >
                    {cellContent}
                </button>
            ),
        },
        {
            dataField: "measure_type",
            text: t("Type"),
            sort: false,
            headerStyle: { width: "140px" },
            style: { width: "140px", whiteSpace: "nowrap" },
            formatter: (cellContent) => t(getMeasureTypeLabel(cellContent)),
        },
        {
            dataField: "controls",
            text: t("Related controls"),
            sort: false,
            headerStyle: { width: "24%", minWidth: "180px" },
            style: { width: "24%", minWidth: "180px", whiteSpace: "normal" },
            formatter: (cellContent) => {
                const controls = getVisibleControls(cellContent);

                if (controls.visible.length === 0) {
                    return "";
                }

                return (
                    <div className="d-flex flex-wrap align-items-center gap-1">
                        {controls.visible.map((control) => (
                            <Badge key={control.id} className="bg-light" color="light" pill>
                                {formatControlChip(control)}
                            </Badge>
                        ))}
                        {controls.remaining > 0 && (
                            <span className="text-muted">
                                {t("+{{count}} more", {
                                    count: controls.remaining,
                                })}
                            </span>
                        )}
                    </div>
                );
            },
        },
        {
            dataField: "evidence_count",
            text: t("Evidence"),
            sort: false,
            headerStyle: { width: "110px" },
            style: { width: "110px" },
            formatter: (cellContent) => cellContent ?? 0,
        },
        {
            dataField: "usage_status",
            text: t("Used by your organization"),
            sort: false,
            headerStyle: { width: "240px", minWidth: "240px", whiteSpace: "normal" },
            style: { width: "240px", minWidth: "240px", whiteSpace: "normal" },
            formatter: (_cellContent, row) => (
                <UsageDecision
                    measure={row}
                    isSaving={pendingMeasureId === row.id}
                    onChange={onUsageChange}
                    t={t}
                />
            ),
        },
        {
            dataField: "actions",
            isDummyField: true,
            text: t("Actions"),
            sort: false,
            headerStyle: { width: "120px" },
            style: { width: "120px", whiteSpace: "nowrap" },
            formatter: (_cellContent, row) => {
                const isDeleting = String(deletingMeasureId) === String(row.id);

                return (
                    <div className="d-flex flex-nowrap align-items-center">
                        <Button
                            id={`positive-list-view-${row.id}`}
                            color="primary"
                            outline
                            className="border-0"
                            aria-label={t("View")}
                            onClick={() => onViewMeasure(row)}
                        >
                            <i className="ri-eye-line font-size-20" />
                        </Button>
                        <UncontrolledTooltip
                            placement="top"
                            target={`positive-list-view-${row.id}`}
                        >
                            {t("View")}
                        </UncontrolledTooltip>

                        <Button
                            id={`positive-list-delete-${row.id}`}
                            color="link"
                            className="text-danger"
                            aria-label={t("Delete")}
                            disabled={isDeleting}
                            onClick={() => onDeleteMeasure(row)}
                        >
                            {isDeleting ? (
                                <Spinner animation="border" size="sm" />
                            ) : (
                                <i className="ri-delete-bin-line font-size-20" />
                            )}
                        </Button>
                        <UncontrolledTooltip
                            placement="top"
                            target={`positive-list-delete-${row.id}`}
                        >
                            {t("Delete")}
                        </UncontrolledTooltip>
                    </div>
                );
            },
        },
    ];

    const NoDataIndication = () => {
        if (!isFetched || loading) {
            return <></>;
        }

        return (
            <div className="alert m-0" role="alert">
                <p className="text-center mb-0">
                    {filtersAreActive
                        ? t("No measures match these filters.")
                        : t(EMPTY_LIST)}
                </p>
            </div>
        );
    };

    return (
        <BootstrapTable
            keyField="id"
            data={items}
            columns={columns}
            remote={{
                pagination: false,
                filter: true,
                sort: false,
                cellEdit: false,
                search: true,
            }}
            loading={loading}
            overlay={overlayFactory({
                spinner: (
                    <Spinner animation="border" variant="primary" size="md" />
                ),
                text: t("Loading..."),
            })}
            bordered={false}
            striped
            responsive
            wrapperClasses="table-responsive positive-list__table"
            classes="table"
            headerWrapperClasses="thead-light"
            noDataIndication={NoDataIndication}
        />
    );
};

export default PositiveListTable;
